import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Search } from '../components/Search';
import { ProductCard } from '../components/ProductCard';
import { Money } from 'shared/build/Money';
import { useQuery } from 'react-query';
import { WebsiteProducts } from 'shared/build/types';
import { customFetch, useQueryString } from '../util/functions';
import { useParams } from 'react-router-dom';
import { useAuthentication } from '../context/AuthenticationProvider';
import { Spinner } from '../components/Spinner';
import { Select } from 'ui-components/build';
import { productCategories } from '../util/constants';

const ScWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ScProductList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  justify-content: center;
`;

const ScOrderContainer = styled.label`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;

  select {
    margin-left: 10px;
  }
`;

const ScCategoryDescriptor = styled.div`
  h5 {
    margin: 0 0 5px;
  }
  p {
    margin: 0 0 20px;
  }
`;

const LOCAL_STORAGE_DEFAULT_SORTING = 'defaultProductSorting';

type OrderType = 'title' | 'price';

const getDefaultSortingForUser = (): OrderType => {
  const sorting = localStorage.getItem(LOCAL_STORAGE_DEFAULT_SORTING);

  if (sorting === 'title' || sorting === 'price') return sorting;

  return 'title';
};

export const Products = () => {
  const { user } = useAuthentication();
  const queryString = useQueryString();
  const { categoryId } = useParams<{ categoryId: string }>();
  const [order, setOrder] = useState<OrderType>(getDefaultSortingForUser());
  const [category, setCategory] = useState<typeof productCategories[0]>();

  useEffect(() => {
    if (!categoryId) return;

    const category = productCategories.find((c) => c.id === categoryId);
    setCategory(category);
  }, [categoryId]);

  const { data, status } = useQuery<WebsiteProducts, string>(
    `website-products-search, ${categoryId || ''}, ${
      queryString.get('term') || ''
    }`,
    async (): Promise<WebsiteProducts> => {
      const qs = new URLSearchParams();
      if (categoryId) {
        qs.set('categoryId', categoryId);
      }

      if (queryString.has('term')) {
        qs.set('term', queryString.get('term') || '');
      }

      const response = await customFetch(
        `/api/website/products?${qs.toString()}`
      );
      if (response.ok) {
        return response.json();
      }
      throw new Error('exploded');
    }
  );

  if (!user) return null;

  return (
    <ScWrapper className="babys-page-padding">
      <Search />
      {category && (
        <ScCategoryDescriptor>
          <h5>{category.title}</h5>
          <p>{category.description}</p>
        </ScCategoryDescriptor>
      )}
      <ScOrderContainer>
        <Select
          onChange={(e) => {
            setOrder(e.target.value as OrderType);
            localStorage.setItem(LOCAL_STORAGE_DEFAULT_SORTING, e.target.value);
          }}
          value={order}
        >
          <option value="title">Ordernado por Nome</option>
          <option value="price">Ordernado por Preço</option>
        </Select>
      </ScOrderContainer>
      {status === 'loading' && <Spinner />}
      <ScProductList>
        {data &&
          data.products
            .sort((a, b) => {
              const vA = order === 'price' ? a.pricing[user.currency] : a.title;
              const vB = order === 'price' ? b.pricing[user.currency] : b.title;

              if (vA < vB) return -1;

              if (vA > vB) return 1;

              return 1;
            })
            .map((product) => (
              <ProductCard
                description={product.title}
                productId={product.id}
                key={product.id}
                price={new Money(product.pricing[user.currency], user.currency)}
                imageId={product.imagesIds[0]}
                origin={category?.title || 'Presentes'}
              />
            ))}
        {data && data.products.length === 0 && (
          <h4>Hmmm... Não encontramos o que você queria. :-(</h4>
        )}
      </ScProductList>
    </ScWrapper>
  );
};
